import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useIntlFormatting } from '../utils/intl';
import type { ParcelSupervisionData } from '../lib/parcelSupervision';

const UNKNOWN_HYDRAULIC_STATE = 'unknown';

/** Display server results only. This component never decides command availability. */
export default function ParcelSupervision({ data }: { data?: ParcelSupervisionData }) {
  return data ? <SupervisionContent data={data} /> : null;
}

function SupervisionContent({ data }: { data: ParcelSupervisionData }) {
  const { t } = useTranslation('common');
  const { formatNumber, formatDateTime } = useIntlFormatting();
  const [now, setNow] = useState(Date.now());
  const received = useMemo(() => Date.now(), [data]);
  useEffect(() => {
    if (!data) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [data]);
  if (!data) return null;
  const fresh = data.observedAt + Math.max(0, now - received) < data.validUntil;
  const cmd = data.lastCommand;
  const flow = data.flow;
  const number = (n: number | null) => n === null ? '—' : formatNumber(n, { maximumFractionDigits: 2 });
  const hydraulic = fresh ? data.hydraulicState : UNKNOWN_HYDRAULIC_STATE;
  const label = hydraulic === 'pressurized' ? t('supervisionPressurePresent')
    : hydraulic === 'low_pressure' ? t('supervisionPressureLow') : t('supervisionUnconfirmed');
  const commandStatus = (status: string) => status === 'OBSERVED_SUCCESS' ? t('supervisionObserved')
    : status === 'OBSERVED_FAILED' ? t('supervisionFailed')
    : status === 'SENT_TO_TTN' ? t('supervisionSent')
    : status === 'TIMEOUT' ? t('supervisionTimeout') : t('supervisionPending');
  return <div className="w-full space-y-1 text-sm" data-testid="parcel-supervision">
    <p>{label}{fresh && data.pressureBar !== null && <> · {number(data.pressureBar)} {t('agrisafeR1:dashboard.units.bar')}</>}</p>
    {fresh && data.flowEvidence === 'detected' && <p>{t('supervisionFlowDetected')}</p>}
    {fresh && data.flowEvidence === 'zero' && <p>{t('supervisionFlowZero')}</p>}
    {fresh && data.agreement === 'disagreement' && <p role="status">{t('supervisionDisagreement')}</p>}
    {fresh && data.agreement === 'consistent_open' && <p>{t('supervisionConsistentOpen')}</p>}
    {fresh && data.agreement === 'consistent_closed' && <p>{t('supervisionConsistentClosed')}</p>}
    {cmd ? <p>{t('supervisionLastCommand')}: {cmd.action === 'open' ? t('supervisionOpen') : t('supervisionClose')}
      {' · '}{formatDateTime(cmd.requestedAt)}{' · '}{commandStatus(cmd.status)}</p>
      : <p>{t('supervisionNoCommand')}</p>}
    {flow && <details>
      <summary>{t('supervisionWater')}</summary>
      <p>{t('supervisionFlow')}: {number(data.observedAt + Math.max(0, now - received) < flow.validUntil ? flow.flowLpm : null)} {t('supervisionFlowUnit')}</p>
      <p>{t('supervisionMeterVolume')}: {number(flow.totalMeterLiters)} L</p>
      <p>{t('supervisionAttributedVolume')}: {number(flow.attributedLiters)} L</p>
      <p>{t('supervisionSharedWarning')}</p>
      <p>{t('supervisionCoverage')}: {formatDateTime(flow.coverageStart)} – {formatDateTime(flow.observedAt)}</p>
      {flow.lastCycle && <p>{t('supervisionLastCycleVolume')}: {number(flow.lastCycle.liters)} L</p>}
      {flow.daily.map(day => <p key={day.day}>{t('supervisionUtcDay', { day: day.day })}: {number(day.attributedLiters)} L</p>)}
    </details>}
  </div>;
}
